import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import API from '@aws-amplify/api';

interface RecalculateStockButtonProps {
  type: 'ingredient' | 'packaging' | 'product' | 'batch';
  id: string | number;
  onSuccess?: () => void;
}

export default function RecalculateStockButton({
  type,
  id,
  onSuccess,
}: RecalculateStockButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<any>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success',
  );

  const handleDryRun = async () => {
    setLoading(true);
    try {
      const response = await API.get('products', '/premix/recalculate-stock', {
        queryStringParameters: {
          type,
          id,
          dryRun: true,
        },
      });
      setDryRunResult(response);
      setOpen(true);
    } catch (error: any) {
      console.error(error);
      setSnackbarMessage('Error calculating stock: ' + error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await API.get('products', '/premix/recalculate-stock', {
        queryStringParameters: {
          type,
          id,
          dryRun: false,
        },
      });
      setSnackbarMessage('Stock recalculation applied successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpen(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error(error);
      setSnackbarMessage('Error applying changes: ' + error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setDryRunResult(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        onClick={handleDryRun}
        disabled={loading || !id}
      >
        {loading ? <CircularProgress size={24} /> : 'Recalculate Stock'}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Recalculate Stock - Dry Run Results</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Review the calculated changes below. Click "Confirm Fix" to apply these changes to the database.
          </DialogContentText>

          {dryRunResult && (
            <Box>
              <Box mb={2}>
                <Typography variant="subtitle1">Master Stock:</Typography>
                <Typography>
                  Current: <strong>{dryRunResult.currentMasterStock}</strong>
                </Typography>
                <Typography>
                  Calculated: <strong>{dryRunResult.calculatedMasterStock}</strong>
                </Typography>
                {dryRunResult.masterStockDiff ? (
                  <Typography color="error" variant="body2">
                    Mismatch detected! Master stock will be updated.
                  </Typography>
                ) : (
                  <Typography style={{ color: 'green' }} variant="body2">
                    Master stock matches calculated history.
                  </Typography>
                )}
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle1">Stock Card History Mismatches ({dryRunResult.cardUpdates} found):</Typography>
                {dryRunResult.details && dryRunResult.details.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Stock Card ID</TableCell>
                        <TableCell>Recorded Latest Stock</TableCell>
                        <TableCell>Calculated Latest Stock</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dryRunResult.details.map((detail: any) => (
                        <TableRow key={detail.stockCardId}>
                          <TableCell>{detail.stockCardId}</TableCell>
                          <TableCell>{detail.oldLatestStock}</TableCell>
                          <TableCell>{detail.newLatestStock}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" style={{ color: 'green' }}>
                    No inconsistencies found in stock card history.
                  </Typography>
                )}
              </Box>
            </Box>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="primary" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Confirm Fix'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <MuiAlert severity={snackbarSeverity} elevation={6} variant="filled">
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
